<template>
  <v-form ref="PROJET" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="PROJET">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <projet-nom v-if="isDisplayed('NOM')"
                    ref="NOM"
                    :theme="theme"
                    :showMissing="showMissing"
                    v-model:value=nom
                    :min="3"
                    :max="75"
                    @status=statusNom
                    :over-ride-placeholder=placeholderNom
                    :required="isRequired('NOM')"
                    :contextId=projet.id
                    :readOnly="readOnly('NOM')"
                    :objType=objType
                    :fiche=fiche
                    :sectionName="sectionName">
        </projet-nom>
        <numero-objet-summary v-if="isDisplayed('NUMERO_OBJET_ID')"
                              ref="NUMERO_OBJET_ID"
                              :theme="theme"
                              :showMissing="showMissing"
                              v-model:value=numeroObjetId
                              :moduleId=moduleId
                              libelle="'Numéro d\'objet'"
                              newLabel=''
                              selectText=""
                              @status=statusNumeroObjetId
                              :autoClear=true
                              :owner="owner"
                              :requiredAttributs="requiredAttributs"
                              :autoSelect=true
                              :ressource=ressourceNumeroObjetSummary
                              :filters=numeroObjetIdFilter
                              :toggleReset=resetNumeroObjetId
                              :required="isRequired('NUMERO_OBJET_ID')"
                              :contextId=projet.id
                              :readOnly="readOnly('NUMERO_OBJET_ID')"
                              :objType=objType
                              :fiche=fiche
                              :sectionName="sectionName">
        </numero-objet-summary>
        <etablissement-summary v-if="isDisplayed('ETABLISSEMENT_ID')"
                               ref="ETABLISSEMENT_ID"
                               :theme="theme"
                               :showMissing="showMissing"
                               v-model:value=etablissementId
                               :moduleId=moduleId
                               libelle="'Établissement'"
                               newLabel=''
                               selectText=""
                               @status=statusEtablissementId
                               :autoClear=true
                               :owner="owner"
                               :requiredAttributs="requiredAttributs"
                               :autoSelect=true
                               :ressource=ressourceEtablissementSummary
                               :filters=etablissementIdFilter
                               :toggleReset=resetEtablissementId
                               :required="isRequired('ETABLISSEMENT_ID')"
                               :contextId=projet.id
                               :readOnly="readOnly('ETABLISSEMENT_ID')"
                               :objType=objType
                               :fiche=fiche
                               :sectionName="sectionName">
        </etablissement-summary>
        <graph-control ref="GRAPH_CONTROL"
                       :obj="getGraphObj"
                       :objType="getGraphObjType()"
                       :sectionName="sectionName"
                       :toggle="toggleGraph"
                       :disabled="readOnly('GRAPH_CONTROL')"
                       v-model:value=graphAnswers
                       @setMinimum="setMinimum"
                       @status="statusGraph">
        </graph-control>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudProjet from '@/generated/cruds/CrudProjet';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import Owner from '@/extend/classes/Owner';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudProjet,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.PROJET,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderNom: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceNumeroObjetSummary () { return RessourceGenControl(GenControl.PROJET_NUMERO_OBJET_SUMMARY); },
      ressourceEtablissementSummary () { return RessourceGenControl(GenControl.PROJET_ETABLISSEMENT_SUMMARY); },

      ressource () { return RessourceGenSection(GenSection.PROJET); },

      owner (): Owner {
        const owner = new Owner();
        owner.objType = ObjTypes.PROJET;
        owner.objId = this.editProjet.id;
        return owner;
      },

      fields (): string[] {
        return ['NOM', 'NUMERO_OBJET_ID', 'ETABLISSEMENT_ID', 'GRAPH_CONTROL'];
      },

      extraButtons (): Button[] {
        return [];
      },
      getGraphObj (): unknown {
        return this.editProjet;
      },


      numeroObjetIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
      etablissementIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editProjet.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("NOM", false);
          this.readOnlyMap.set("NUMERO_OBJET_ID", false);
          this.readOnlyMap.set("ETABLISSEMENT_ID", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("NOM", true);
          this.displayMap.set("NUMERO_OBJET_ID", this.requiredMissing(requiredAttributs, "NUMERO_OBJET_ID") || !!this.fiche);
          this.displayMap.set("ETABLISSEMENT_ID", true);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("NOM", true);
          this.requiredMap.set("NUMERO_OBJET_ID", this.requiredMissing(requiredAttributs, "NUMERO_OBJET_ID") || !this.fiche);
          this.requiredMap.set("ETABLISSEMENT_ID", true);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoProjet' 
      },

      getGraphObjType (): ObjTypes {
        return this.objType;
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
